import { useEffect, useState } from "react";
import { Download, RefreshCw, Database, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/auth";

/**
 * BackupPanel — admin tab showing every PDF backup ever generated,
 * with a "Generate now" button and a Download link per row. Backups
 * are also auto-created on every backend startup (debounced 6h).
 */

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

const fmtSize = (b) => {
  if (!b && b !== 0) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtCounts = (c) => {
  if (!c) return "—";
  const total = Object.values(c).reduce((a, b) => a + Number(b || 0), 0);
  return `${total} rows across ${Object.keys(c).length} tables`;
};

const BackupPanel = () => {
  const [rows, setRows] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanSummary, setScanSummary] = useState(null);

  const reload = async () => {
    try {
      const { data } = await api.get("/admin/backups");
      setRows(data?.backups || []);
    } catch (err) {
      toast(err?.response?.data?.detail || "Could not load backups", {
        duration: 2600,
      });
      setRows([]);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/backups");
      toast(`Backup created · ${data?.filename || "PDF ready"}`, {
        duration: 2600,
      });
      reload();
    } catch (err) {
      toast(err?.response?.data?.detail || "Backup failed", { duration: 3000 });
    } finally {
      setGenerating(false);
    }
  };

  const scan = async () => {
    setScanning(true);
    setScanSummary(null);
    try {
      const { data } = await api.post("/admin/media-health/scan");
      setScanSummary(data);
      toast(
        `Media scan complete · ${data.healthy} healthy · ${data.broken} broken`,
        { duration: 3400 }
      );
    } catch (err) {
      toast(err?.response?.data?.detail || "Scan failed", { duration: 3000 });
    } finally {
      setScanning(false);
    }
  };

  const downloadHref = (id) => `${API}/admin/backups/${id}/download`;

  return (
    <div data-testid="section-backup" className="space-y-8">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
            System · Backup archive
          </span>
          <h1 className="text-3xl md:text-4xl mt-2 text-[var(--ld-ink)]">
            Backup
          </h1>
          <p className="text-sm text-[var(--ld-ink-muted)] mt-2 max-w-2xl">
            Every backend restart / redeploy generates a fresh PDF snapshot of
            the operational dataset (debounced to once every 6h). You can also
            trigger one on demand — handy right before making bulk edits.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={reload}
            data-testid="backup-refresh"
            className="inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.22em] uppercase border border-[var(--ld-ink)]/40 hover:border-[var(--ld-gold-deep)] hover:text-[var(--ld-gold-deep)] transition-colors"
          >
            <RefreshCw size={13} strokeWidth={1.6} />
            Refresh
          </button>
          <button
            type="button"
            onClick={generate}
            disabled={generating}
            data-testid="backup-generate"
            className="inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.22em] uppercase bg-[var(--ld-ink)] text-[var(--ld-gold)] hover:bg-black disabled:opacity-60 disabled:cursor-wait"
          >
            <Database size={13} strokeWidth={1.6} />
            {generating ? "Generating…" : "Backup now"}
          </button>
        </div>
      </div>

      <div className="border border-[var(--ld-border)]">
        {rows === null ? (
          <p className="p-6 text-sm text-[var(--ld-ink-muted)]">
            Loading backups…
          </p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-[var(--ld-ink-muted)]">
            No backups yet. Click <b>Backup now</b> to create the first one.
          </p>
        ) : (
          <table className="w-full text-[13px]" data-testid="backup-table">
            <thead className="bg-[var(--ld-ink)]/5 text-[var(--ld-ink-muted)] text-[10px] tracking-[0.18em] uppercase">
              <tr>
                <th className="text-left px-4 py-2">Generated</th>
                <th className="text-left px-4 py-2">Trigger</th>
                <th className="text-left px-4 py-2">Contents</th>
                <th className="text-left px-4 py-2">Size</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`backup-row-${r.id}`}
                  className="border-t border-[var(--ld-border)]"
                >
                  <td className="px-4 py-3">{fmtDate(r.created_at)}</td>
                  <td className="px-4 py-3 text-[var(--ld-ink-muted)]">
                    {r.trigger}
                  </td>
                  <td className="px-4 py-3 text-[var(--ld-ink-muted)]">
                    {fmtCounts(r.counts)}
                  </td>
                  <td className="px-4 py-3 text-[var(--ld-ink-muted)]">
                    {fmtSize(r.size_bytes)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <a
                      href={downloadHref(r.id)}
                      data-testid={`backup-download-${r.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase border border-[var(--ld-gold-deep)] text-[var(--ld-gold-deep)] hover:bg-[var(--ld-gold-deep)] hover:text-white transition-colors"
                    >
                      <Download size={12} strokeWidth={1.7} />
                      Download PDF
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Media health scan — related admin utility */}
      <div className="border border-[var(--ld-border)] p-6 mt-10">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <p className="font-accent text-[10px] tracking-[0.28em] uppercase text-[var(--ld-gold-deep)]">
              Media health
            </p>
            <h2 className="text-lg mt-1 text-[var(--ld-ink)]">
              Scan vendor uploads
            </h2>
            <p className="text-sm text-[var(--ld-ink-muted)] mt-1 max-w-xl">
              Checks every vendor&apos;s uploaded photos/videos against object
              storage. Vendors with missing files get a red dot in the
              Vendor list so you know who to nudge for a re-upload.
            </p>
          </div>
          <button
            type="button"
            onClick={scan}
            disabled={scanning}
            data-testid="media-health-scan"
            className="inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.22em] uppercase border border-red-700/50 text-red-800 hover:bg-red-800 hover:text-white disabled:opacity-60 disabled:cursor-wait transition-colors"
          >
            <ShieldAlert size={13} strokeWidth={1.6} />
            {scanning ? "Scanning…" : "Scan now"}
          </button>
        </div>
        {scanSummary && (
          <p
            data-testid="media-health-summary"
            className="mt-4 text-sm text-[var(--ld-ink)]"
          >
            Scanned <b>{scanSummary.scanned}</b> vendor
            {scanSummary.scanned === 1 ? "" : "s"} · <span className="text-emerald-700">{scanSummary.healthy} healthy</span>
            {" · "}
            <span className="text-red-800">
              {scanSummary.broken} broken
            </span>
          </p>
        )}
      </div>
    </div>
  );
};

export default BackupPanel;
